import React, { useState, Component } from 'react';
import { ClockIcon, InboxIcon, CogIcon, ShieldCheckIcon, GitMergeIcon, CheckCircleIcon, CloudIcon, CheckIcon, AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, UserIcon, BotIcon, LogInIcon, FileTextIcon, ShoppingCartIcon, UsersIcon, FolderIcon, SettingsIcon, BarChartIcon, CircleIcon } from 'lucide-react';
// Types
export type BusinessEventCategory = 'received' | 'processing' | 'validation' | 'matching' | 'approval' | 'integration' | 'completed' | 'error' | 'authentication' | 'invoice' | 'procurement' | 'vendor' | 'document' | 'settings' | 'reporting';
export type ActorType = 'user' | 'system';
export interface BusinessEvent {
  id: string;
  timestamp: string;
  description: string;
  actor: string;
  actorType: ActorType;
  category: BusinessEventCategory;
  details?: string;
}
export interface BusinessHistoryProps {
  entityType: string;
  entityId: string;
  events: BusinessEvent[];
  locale?: string;
}
// Category configuration
const categoryConfig: Record<string, {
  color: string;
  bgColor: string;
  icon: React.ElementType;
  label: string;
}> = {
  received: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: InboxIcon,
    label: 'Received'
  },
  processing: {
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    icon: CogIcon,
    label: 'Processing'
  },
  validation: {
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    icon: ShieldCheckIcon,
    label: 'Validation'
  },
  matching: {
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    icon: GitMergeIcon,
    label: 'Matching'
  },
  approval: {
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    icon: CheckCircleIcon,
    label: 'Approval'
  },
  integration: {
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
    icon: CloudIcon,
    label: 'Integration'
  },
  completed: {
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    icon: CheckIcon,
    label: 'Completed'
  },
  error: {
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    icon: AlertCircleIcon,
    label: 'Error'
  },
  authentication: {
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    icon: LogInIcon,
    label: 'Authentication'
  },
  invoice: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    icon: FileTextIcon,
    label: 'Invoice'
  },
  procurement: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: ShoppingCartIcon,
    label: 'Procurement'
  },
  vendor: {
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
    icon: UsersIcon,
    label: 'Vendor'
  },
  document: {
    color: 'text-violet-600',
    bgColor: 'bg-violet-100',
    icon: FolderIcon,
    label: 'Document'
  },
  settings: {
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: SettingsIcon,
    label: 'Settings'
  },
  reporting: {
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
    icon: BarChartIcon,
    label: 'Reporting'
  }
};
// Fallback config for any unrecognized category
const defaultCategoryConfig = {
  color: 'text-gray-600',
  bgColor: 'bg-gray-100',
  icon: CircleIcon,
  label: 'Activity'
};
// Sample mock data for demonstration
export const sampleInvoiceBusinessEvents: BusinessEvent[] = [{
  id: 'evt-001',
  timestamp: '2024-03-04T14:32:00Z',
  description: 'Invoice posted to ERP',
  actor: 'ERP Integration Service',
  actorType: 'system',
  category: 'completed',
  details: 'Successfully posted to SAP with document number 4500012345'
}, {
  id: 'evt-002',
  timestamp: '2024-03-04T14:15:00Z',
  description: 'Approval granted',
  actor: 'Sarah Johnson',
  actorType: 'user',
  category: 'approval',
  details: 'Final approval granted. Invoice cleared for payment.'
}, {
  id: 'evt-003',
  timestamp: '2024-03-04T11:45:00Z',
  description: 'Approval requested',
  actor: 'Workflow Engine',
  actorType: 'system',
  category: 'approval',
  details: 'Routed to Sarah Johnson (Finance Manager) for approval based on amount threshold.'
}, {
  id: 'evt-004',
  timestamp: '2024-03-04T11:30:00Z',
  description: 'Three-way matching completed',
  actor: 'Matching Engine',
  actorType: 'system',
  category: 'matching',
  details: 'Invoice matched against PO-34821 and GRN-78432. Variance: 0.5% within tolerance.'
}, {
  id: 'evt-005',
  timestamp: '2024-03-04T11:15:00Z',
  description: 'Validation passed',
  actor: 'Validation Service',
  actorType: 'system',
  category: 'validation',
  details: 'All mandatory fields validated. Tax calculations verified. No duplicate detected.'
}, {
  id: 'evt-006',
  timestamp: '2024-03-04T10:45:00Z',
  description: 'Vendor identified',
  actor: 'Vendor Matching Service',
  actorType: 'system',
  category: 'processing',
  details: 'Matched to vendor "Dell Technologies" (VEN-00234) with 98% confidence.'
}, {
  id: 'evt-007',
  timestamp: '2024-03-04T10:30:00Z',
  description: 'OCR extraction completed',
  actor: 'Document Intelligence',
  actorType: 'system',
  category: 'processing',
  details: 'Extracted 12 fields with 95% average confidence. 2 fields flagged for review.'
}, {
  id: 'evt-008',
  timestamp: '2024-03-04T10:21:00Z',
  description: 'Invoice received',
  actor: 'Email Ingestion Service',
  actorType: 'system',
  category: 'received',
  details: 'Received via email from ap@dell.com. File: INV-2024-0892.pdf (245 KB)'
}];
export function BusinessHistory({
  entityType,
  entityId,
  events,
  locale = 'en-US'
}: BusinessHistoryProps) {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const toggleExpand = (eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  const sortedEvents = [...events].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  if (events.length === 0) {
    return <div className="bg-white rounded-lg border border-gray-200" data-id="element-2058">
        <div className="px-6 py-12 text-center" data-id="element-2059">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-2060" />
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-2061">No history</h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2062">
            No events have been recorded for this {entityType.toLowerCase()}{' '}
            yet.
          </p>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-lg border border-gray-200" data-id="element-2063">
      <div className="px-6 py-4 border-b border-gray-200" data-id="element-2064">
        <h3 className="text-base font-semibold text-gray-900" data-id="element-2065">
          {entityType} History
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-2066">
          Timeline of events for {entityType} {entityId}
        </p>
      </div>

      <div className="px-6 py-6" data-id="element-2067">
        <div className="flow-root" data-id="element-2068">
          <ul className="-mb-8" role="list" aria-label="Event timeline" data-id="element-2069">
            {sortedEvents.map((event, eventIdx) => {
            const config = categoryConfig[event.category] || defaultCategoryConfig;
            const IconComponent = config.icon;
            const isExpanded = expandedEvents.has(event.id);
            const isLast = eventIdx === sortedEvents.length - 1;
            return <li key={event.id} data-id="element-2070">
                  <div className="relative pb-8" data-id="element-2071">
                    {/* Connecting line */}
                    {!isLast && <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-2072" />}

                    <div className="relative flex items-start space-x-3" data-id="element-2073">
                      {/* Icon */}
                      <div data-id="element-2074">
                        <span className={`h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white ${config.bgColor}`} data-id="element-2075">
                          <IconComponent className={`h-5 w-5 ${config.color}`} aria-hidden="true" data-id="element-2076" />
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0" data-id="element-2077">
                        <div className="flex items-center justify-between" data-id="element-2078">
                          <div data-id="element-2079">
                            <p className="text-sm font-medium text-gray-900" data-id="element-2080">
                              {event.description}
                            </p>
                            <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500" data-id="element-2081">
                              {event.actorType === 'user' ? <UserIcon className="h-4 w-4 text-gray-400" data-id="element-2082" /> : <BotIcon className="h-4 w-4 text-gray-400" data-id="element-2083" />}
                              <span data-id="element-2084">{event.actor}</span>
                              <span className="text-gray-300" data-id="element-2085">•</span>
                              <time dateTime={event.timestamp} data-id="element-2086">
                                {formatTimestamp(event.timestamp)}
                              </time>
                            </div>
                          </div>

                          {/* Category badge */}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`} data-id="element-2087">
                            {config.label}
                          </span>
                        </div>

                        {/* Expandable details */}
                        {event.details && <div className="mt-2" data-id="element-2088">
                            <button onClick={() => toggleExpand(event.id)} className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800" aria-expanded={isExpanded} data-id="element-2089">
                              {isExpanded ? <>
                                  <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-2090" />
                                  Hide details
                                </> : <>
                                  <ChevronDownIcon className="h-4 w-4 mr-1" data-id="element-2091" />
                                  Show details
                                </>}
                            </button>

                            {isExpanded && <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm text-gray-700" data-id="element-2092">
                                {event.details}
                              </div>}
                          </div>}
                      </div>
                    </div>
                  </div>
                </li>;
          })}
          </ul>
        </div>
      </div>
    </div>;
}